import React, { useEffect, useState } from 'react';
import { validateAssayAnalytes, ANALYTE_KINDS, type AnalyteKind, type AssayAnalytes, type AssayAnalytesDynamic } from '@mariana/labos-client';
import { patchAssay, apiErrorMessage } from '../../api/limsClient';
import { definitionFromRows, rowsFromDefinition, workedExamplesPass, type AnalyteRow } from '../../utils/assayDefinition';
import { AnalytesTable } from './AnalytesTable';
import './AssayDefinition.css';

interface Props {
  assayId: number;
  /** The saved definition when it passes the validator; null when the assay has none or it does not. */
  definition: AssayAnalytes | null;
  /** The stored value when it does not pass the validator; edited as JSON until it does. */
  invalidDefinition?: unknown;
  isAdmin: boolean;
  onSaved: () => Promise<void> | void;
}

interface DynamicDraft {
  enabled: boolean;
  keyPattern: string;
  nameFrom: 'label' | 'key';
  kind: AnalyteKind;
  unitFromEntry: boolean;
}

const dynamicDraftOf = (d: AssayAnalytesDynamic | undefined): DynamicDraft => ({
  enabled: !!d,
  keyPattern: d?.keyPattern ?? '',
  nameFrom: d?.nameFrom ?? 'key',
  kind: d?.kind ?? 'result',
  unitFromEntry: d?.unitFrom === 'entry',
});

const dynamicOf = (d: DynamicDraft): AssayAnalytesDynamic | null =>
  d.enabled ? { keyPattern: d.keyPattern, nameFrom: d.nameFrom, kind: d.kind, ...(d.unitFromEntry ? { unitFrom: 'entry' as const } : {}) } : null;

const pretty = (def: unknown) => JSON.stringify(def ?? { version: 1, analytes: [] }, null, 2);

/**
 * The analytes definition as a table or as raw JSON. Both save through the
 * generic assay PATCH, so the API's validator has the last word; its message
 * is shown under the editor. The table pre-checks with the same validator
 * (same package, same message) to save a round trip.
 */
export const AssayDefinitionEditor: React.FC<Props> = ({ assayId, definition, invalidDefinition, isAdmin, onSaved }) => {
  const [view, setView] = useState<'table' | 'json'>(invalidDefinition !== undefined ? 'json' : 'table');
  const [rows, setRows] = useState<AnalyteRow[]>(() => rowsFromDefinition(definition));
  const [strict, setStrict] = useState<boolean>(definition?.strict === true);
  const [dynamic, setDynamic] = useState<DynamicDraft>(() => dynamicDraftOf(definition?.dynamic));
  const [json, setJson] = useState<string>(() => pretty(invalidDefinition ?? definition));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // A fresh definition from the server resets the editor unless the operator is mid-edit.
  useEffect(() => {
    if (dirty) return;
    setRows(rowsFromDefinition(definition));
    setStrict(definition?.strict === true);
    setDynamic(dynamicDraftOf(definition?.dynamic));
    setJson(pretty(invalidDefinition ?? definition));
    if (invalidDefinition !== undefined) setView('json');
  }, [definition, invalidDefinition]); // eslint-disable-line react-hooks/exhaustive-deps

  const touch = () => { setDirty(true); setSaved(false); setError(null); };

  const examplesOk = workedExamplesPass(rows);

  const save = async (value: unknown) => {
    setSaving(true);
    setError(null);
    try {
      await patchAssay(assayId, { analytes: value });
      setDirty(false);
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveTable = () => {
    const def = definitionFromRows(rows, { strict, dynamic: dynamicOf(dynamic) });
    const checked = validateAssayAnalytes(def);
    if ('error' in checked) { setError(checked.error); return; }
    void save(def);
  };

  const saveJson = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch (err) {
      setError(`Not valid JSON: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    void save(parsed);
  };

  const reset = () => {
    setRows(rowsFromDefinition(definition));
    setStrict(definition?.strict === true);
    setDynamic(dynamicDraftOf(definition?.dynamic));
    setJson(pretty(invalidDefinition ?? definition));
    setDirty(false);
    setError(null);
  };

  const switchView = (next: 'table' | 'json') => {
    if (next === view) return;
    if (dirty && next === 'json') {
      setJson(JSON.stringify(definitionFromRows(rows, { strict, dynamic: dynamicOf(dynamic) }), null, 2));
    }
    // A stored definition the validator rejects must be repaired as JSON before the table can show it.
    if ((dirty || invalidDefinition !== undefined) && next === 'table') {
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch (err) {
        setError(`Not valid JSON: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
      const checked = validateAssayAnalytes(parsed);
      if ('error' in checked) { setError(checked.error); return; }
      const def = checked.value as AssayAnalytes;
      setRows(rowsFromDefinition(def));
      setStrict(def.strict === true);
      setDynamic(dynamicDraftOf(def.dynamic));
    }
    setView(next);
    setError(null);
  };

  return (
    <div className="assay-section">
      <h3 className="assay-section-heading">
        Definition
        <span className="assay-subtabs">
          <button type="button" className={`assay-subtab ${view === 'table' ? 'active' : ''}`} onClick={() => switchView('table')}>Table</button>
          <button type="button" className={`assay-subtab ${view === 'json' ? 'active' : ''}`} onClick={() => switchView('json')}>JSON</button>
        </span>
        {!definition && invalidDefinition === undefined && <span className="assay-muted">no definition yet</span>}
        {invalidDefinition !== undefined && <span className="assay-muted">the stored definition does not pass the current validator; repair it here</span>}
      </h3>

      {view === 'table' ? (
        <>
          <AnalytesTable rows={rows} onChange={next => { setRows(next); touch(); }} />
          <div className="assay-inline-fields">
            <label title="Reject worksheet writes carrying undeclared keys (default: warn only). Engineering's flag.">
              <input type="checkbox" checked={strict} disabled={!isAdmin} onChange={e => { setStrict(e.target.checked); touch(); }} /> strict
            </label>
            <label title="Accept open-ended keys matching a pattern (ICP element families, Camsizer rows)">
              <input type="checkbox" checked={dynamic.enabled} onChange={e => { setDynamic({ ...dynamic, enabled: e.target.checked }); touch(); }} /> dynamic keys
            </label>
            {dynamic.enabled && (
              <>
                <label>pattern
                  <input className="assay-input mono" style={{ width: '18rem' }} placeholder="^report_export_[a-z]{1,2}$" value={dynamic.keyPattern} onChange={e => { setDynamic({ ...dynamic, keyPattern: e.target.value }); touch(); }} />
                </label>
                <label>name from
                  <select className="assay-select" value={dynamic.nameFrom} onChange={e => { setDynamic({ ...dynamic, nameFrom: e.target.value as 'label' | 'key' }); touch(); }}>
                    <option value="key">key</option>
                    <option value="label">label</option>
                  </select>
                </label>
                <label>kind
                  <select className="assay-select" value={dynamic.kind} onChange={e => { setDynamic({ ...dynamic, kind: e.target.value as AnalyteKind }); touch(); }}>
                    {ANALYTE_KINDS.filter(k => k !== 'constant').map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
                <label title="Take the unit from each written entry">
                  <input type="checkbox" checked={dynamic.unitFromEntry} onChange={e => { setDynamic({ ...dynamic, unitFromEntry: e.target.checked }); touch(); }} /> unit from entry
                </label>
              </>
            )}
          </div>
          <div className="assay-actions">
            <button type="button" className="assay-btn primary" disabled={!dirty || saving || !examplesOk} onClick={saveTable}
              title={!examplesOk ? 'Every new or changed formula needs a passing worked example' : undefined}>
              {saving ? 'Saving…' : 'Save definition'}
            </button>
            <button type="button" className="assay-btn" disabled={!dirty || saving} onClick={reset}>Reset</button>
            {!examplesOk && <span className="assay-muted">A new or changed formula needs a passing worked example before saving.</span>}
            {saved && !dirty && <span className="assay-success">Saved</span>}
          </div>
        </>
      ) : (
        <>
          <textarea
            className="assay-textarea assay-json-editor"
            value={json}
            spellCheck={false}
            aria-label="Definition JSON"
            onChange={e => { setJson(e.target.value); touch(); }}
          />
          <div className="assay-actions">
            <button type="button" className="assay-btn primary" disabled={!dirty || saving} onClick={saveJson}>{saving ? 'Saving…' : 'Save JSON'}</button>
            <button type="button" className="assay-btn" disabled={!dirty || saving} onClick={reset}>Reset</button>
            <span className="assay-muted">Sent as-is; the API's validator decides.</span>
            {saved && !dirty && <span className="assay-success">Saved</span>}
          </div>
        </>
      )}
      {error && <div className="assay-error" role="alert">{error}</div>}
    </div>
  );
};
